import { FormEvent, Fragment, useEffect, useState } from 'react';
import type { AdminAddonCode, AdminOutletModule, AdminPlanCode, AdminPlanLimitCode, AdminPlanRow, AdminPlansResponse, AdminPlanVersion, AdminSubscriptionSet } from '@kassa/shared';
import { adminApi, AdminApiError } from '../../api/admin';

// Paketlər (KT-72): qiymət, limitlər, modullar — hər dəyişiklik yeni versiya; əlavə vahidlərin qiyməti.
// Pul onluq sətir kimi göndərilir ("29.90"); boş — qiymət hələ qoyulmayıb.

const MONEY = /^\d{1,8}([.,]\d{1,2})?$/;
const LIMITS: [AdminPlanLimitCode, string][] = [
  ['registers', 'Kassa'],
  ['users', 'İstifadəçi'],
  ['warehouses', 'Anbar'],
  ['products', 'Mal'],
];
export const ADDON_LABEL: Record<AdminAddonCode, string> = {
  extra_register: 'Əlavə kassa',
  extra_user: 'Əlavə istifadəçi',
  extra_warehouse: 'Əlavə anbar',
  ekassa: 'E-kassa',
  whatsapp: 'WhatsApp',
  ai_balance: 'AI balansı (₼)',
};

function errText(err: unknown): string {
  return err instanceof AdminApiError ? err.message : 'Şəbəkə xətası. Yenidən cəhd edin.';
}

/** «29,9» → «29.90»; boş → null; yanlış → undefined. */
export function normMoney(v: string): string | null | undefined {
  const s = v.trim();
  if (s === '') return null;
  if (!MONEY.test(s)) return undefined;
  const [i, f = ''] = s.replace(',', '.').split('.');
  return `${i}.${(f + '00').slice(0, 2)}`;
}

function money(v: string | null): string {
  if (v === null) return '—';
  const [i, f] = v.split('.');
  return `${i.replace(/\B(?=(\d{3})+(?!\d))/g, ' ')},${f ?? '00'} ₼`;
}

function LimitList({ l }: { l: AdminPlanVersion['limits'] }) {
  return (
    <ul className="limit-list">
      {LIMITS.map(([k, label]) => (
        <li key={k}>
          {label}: {l[k] === null || l[k] === undefined ? '∞' : l[k]}
        </li>
      ))}
    </ul>
  );
}

export function Packages() {
  const [data, setData] = useState<AdminPlansResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<AdminPlanCode | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  function load() {
    adminApi
      .plans()
      .then(setData)
      .catch((err) => setError(errText(err)));
  }
  useEffect(load, []);

  return (
    <>
      <section className="card card-wide">
        <div className="toolbar">
          <h2>Paketlər</h2>
        </div>
        <p className="muted small">Dəyişiklik yeni versiya kimi saxlanır: mövcud abunələr bu ay köhnə qiymətlə qalır, yeni qiymət növbəti aydan. İlk qiymət dərhal qüvvəyə minir.</p>
        {notice && <p className="ok" role="status">{notice}</p>}
        {error && <p className="bad" role="alert">{error}</p>}
        {!data && !error && <p className="muted">Yüklənir…</p>}
        {data && (
          <div className="table-scroll">
          <table className="plain">
            <thead>
              <tr>
                <th>Paket</th>
                <th className="num">Aylıq qiymət</th>
                <th>Limitlər</th>
                <th>Modullar</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {data.plans.map((p) => (
                <tr key={p.code}>
                  <td>
                    <strong>{p.current.name}</strong>
                    <div className="muted small">versiya {p.current.version}</div>
                  </td>
                  <td className="num nowrap">
                    {money(p.current.priceMonth)}
                    {p.next && (
                      <div className="muted small">
                        {p.next.validFrom}-dən:
                        <br />
                        {money(p.next.priceMonth)}
                      </div>
                    )}
                  </td>
                  <td className="small">
                    <LimitList l={(p.next ?? p.current).limits} />
                  </td>
                  <td className="small">{(p.next ?? p.current).modules.length} modul</td>
                  <td>
                    <button type="button" className="btn-small" onClick={() => { setEditing(p.code); setNotice(null); }}>
                      Redaktə
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        {data && editing && (
          <PlanForm
            row={data.plans.find((p) => p.code === editing)!}
            modules={data.modules}
            onCancel={() => setEditing(null)}
            onSaved={(v) => {
              setEditing(null);
              setNotice(`Saxlanıldı: versiya ${v}.`);
              load();
            }}
          />
        )}
      </section>
      {data && <Addons rows={data.addons} onSaved={load} />}
    </>
  );
}

function PlanForm({ row, modules, onCancel, onSaved }: { row: AdminPlanRow; modules: { code: string; name: string }[]; onCancel: () => void; onSaved: (v: number) => void }) {
  const base = row.next ?? row.current;
  const [name, setName] = useState(base.name);
  const [price, setPrice] = useState(base.priceMonth ?? '');
  const [limits, setLimits] = useState<Record<AdminPlanLimitCode, string>>(() => {
    const out = {} as Record<AdminPlanLimitCode, string>;
    for (const [k] of LIMITS) out[k] = base.limits[k] === null || base.limits[k] === undefined ? '' : String(base.limits[k]);
    return out;
  });
  const [mods, setMods] = useState<string[]>(base.modules);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const priceMonth = normMoney(price);
    if (priceMonth === undefined) return setError('Qiymət «29,90» formasında olmalıdır');
    const lim: AdminPlanVersion['limits'] = {};
    for (const [k, label] of LIMITS) {
      const v = limits[k].trim();
      if (v === '') lim[k] = null;
      else if (/^\d{1,7}$/.test(v)) lim[k] = Number(v);
      else return setError(`${label}: tam ədəd və ya boş (limitsiz)`);
    }
    setBusy(true);
    setError(null);
    try {
      const r = await adminApi.planSave(row.code, { name, priceMonth, limits: lim, modules: mods });
      onSaved(r.version);
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card lock-panel form-stack" onSubmit={submit} noValidate>
      <h3>{base.name} — yeni versiya</h3>
      <label className="field">
        Ad
        <input value={name} onChange={(e) => setName(e.target.value)} maxLength={60} disabled={busy} />
      </label>
      <label className="field">
        Aylıq qiymət, ₼ (boş — hələ yoxdur)
        <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} placeholder="29,90" disabled={busy} />
      </label>
      <fieldset className="fieldset">
        <legend>Limitlər (boş — limitsiz)</legend>
        <div className="limit-grid">
          {LIMITS.map(([k, label]) => (
            <label key={k} className="field">
              {label}
              <input inputMode="numeric" value={limits[k]} onChange={(e) => setLimits({ ...limits, [k]: e.target.value })} disabled={busy} />
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="fieldset">
        <legend>Modullar</legend>
        <div className="check-grid">
          {modules.map((m) => (
            <label key={m.code} className="check">
              <input
                type="checkbox"
                checked={mods.includes(m.code)}
                onChange={(e) => setMods(e.target.checked ? [...mods, m.code] : mods.filter((x) => x !== m.code))}
                disabled={busy}
              />
              {m.name}
            </label>
          ))}
        </div>
      </fieldset>
      {error && <p className="bad" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="submit" className="btn-primary btn-small" disabled={busy || !name.trim()}>
          {busy ? 'Saxlanılır…' : 'Yeni versiya kimi saxla'}
        </button>
        <button type="button" className="btn-small" onClick={onCancel} disabled={busy}>
          Ləğv et
        </button>
      </div>
    </form>
  );
}

function Addons({ rows, onSaved }: { rows: AdminPlansResponse['addons']; onSaved: () => void }) {
  const [values, setValues] = useState<Record<string, string>>(() => Object.fromEntries(rows.map((r) => [r.code, r.priceMonth ?? ''])));
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  async function save(code: string) {
    const v = normMoney(values[code] ?? '');
    if (v === undefined) return setMsg({ ok: false, text: 'Qiymət «10,00» formasında olmalıdır' });
    setBusy(code);
    try {
      await adminApi.addonPrice(code, v);
      setMsg({ ok: true, text: 'Saxlanıldı.' });
      onSaved();
    } catch (err) {
      setMsg({ ok: false, text: errText(err) });
    } finally {
      setBusy(null);
    }
  }

  return (
    <section className="card card-wide">
      <h2>Əlavə vahidlər</h2>
      <p className="muted small">Paketin üstünə obyekt üzrə əlavə olunur (məs. +2 kassa). Aylıq hesaba avtomatik düşür.</p>
      {msg && <p className={msg.ok ? 'ok' : 'bad'} role="status">{msg.text}</p>}
      <div className="table-scroll">
      <table className="plain">
        <thead>
          <tr>
            <th>Vahid</th>
            <th className="num">Aylıq qiymət, ₼</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <Fragment key={r.code}>
            {r.kind === 'module' && rows[i - 1]?.kind !== 'module' && (
              <tr>
                <th colSpan={3}>Əlavə modullar — paketdə olmayan modul obyektə açılanda aylıq hesaba düşür</th>
              </tr>
            )}
            <tr>
              <td>
                {r.name} <span className="muted small">/ {r.unit}</span>
              </td>
              <td className="num">
                <input
                  className="input-money"
                  inputMode="decimal"
                  aria-label={`${r.name} qiyməti`}
                  value={values[r.code] ?? ''}
                  onChange={(e) => setValues({ ...values, [r.code]: e.target.value })}
                  disabled={busy !== null}
                />
              </td>
              <td>
                <button type="button" className="btn-small" onClick={() => save(r.code)} disabled={busy !== null}>
                  {busy === r.code ? '…' : 'Saxla'}
                </button>
              </td>
            </tr>
            </Fragment>
          ))}
        </tbody>
      </table>
      </div>
    </section>
  );
}

// Obyektin abunəsi (müştərilər siyahısından): paket, status, sınaq tarixi, endirim, əlavə vahidlər
export function SubscriptionEditor({
  tenantId,
  outlet,
  onCancel,
  onSaved,
}: {
  tenantId: string;
  outlet: { id: string; name: string; plan: string | null; subStatus: string | null; trialUntil: string | null; discountPct: string | null; addons: Record<string, number> };
  onCancel: () => void;
  onSaved: () => void;
}) {
  const [plan, setPlan] = useState<AdminPlanCode>((outlet.plan as AdminPlanCode | null) ?? 'simple');
  const [status, setStatus] = useState<AdminSubscriptionSet['status']>(
    outlet.subStatus === 'trial' || outlet.subStatus === 'frozen' ? outlet.subStatus : 'active',
  );
  const [trialUntil, setTrialUntil] = useState(outlet.trialUntil ?? '');
  const [discount, setDiscount] = useState(outlet.discountPct ?? '0');
  const [addons, setAddons] = useState<Record<string, string>>(() =>
    Object.fromEntries((Object.keys(ADDON_LABEL) as AdminAddonCode[]).map((k) => [k, String(outlet.addons[k] ?? 0)])),
  );
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Modullar (hissə 5): paketdən gələnlər işarəli; dəyişdirilənlər (dirty) saxlanır — paketin özü dəyişmir
  const [mods, setMods] = useState<AdminOutletModule[] | null>(null);
  const [dirty, setDirty] = useState<Record<string, boolean>>({});
  useEffect(() => {
    adminApi
      .outletModules(tenantId, outlet.id)
      .then(setMods)
      .catch(() => setMods([]));
  }, [tenantId, outlet.id]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    const add: AdminSubscriptionSet['addons'] = {};
    for (const k of Object.keys(ADDON_LABEL) as AdminAddonCode[]) {
      const v = (addons[k] ?? '').trim() || '0';
      if (!/^\d{1,4}$/.test(v)) return setError(`${ADDON_LABEL[k]}: tam ədəd`);
      add[k] = Number(v);
    }
    const d = discount.trim().replace(',', '.') || '0';
    if (!/^\d{1,3}(\.\d{1,2})?$/.test(d) || Number(d) > 100) return setError('Endirim 0–100 olmalıdır');
    if (status === 'trial' && !trialUntil) return setError('Sınağın son tarixini seçin');
    setBusy(true);
    setError(null);
    try {
      await adminApi.subscriptionSet(tenantId, outlet.id, { plan, status, trialUntil: status === 'trial' ? trialUntil : undefined, discountPct: d, addons: add });
      if (Object.keys(dirty).length > 0) await adminApi.outletModulesSet(tenantId, outlet.id, dirty);
      onSaved();
    } catch (err) {
      setError(errText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card lock-panel form-stack" onSubmit={submit} noValidate>
      <h3>Abunə — {outlet.name}</h3>
      <div className="limit-grid">
        <label className="field">
          Paket
          <select value={plan} onChange={(e) => setPlan(e.target.value as AdminPlanCode)} disabled={busy}>
            <option value="simple">Sadə</option>
            <option value="standard">Standart</option>
            <option value="full">Tam</option>
            <option value="custom">Özün qur</option>
          </select>
        </label>
        <label className="field">
          Status
          <select value={status} onChange={(e) => setStatus(e.target.value as AdminSubscriptionSet['status'])} disabled={busy}>
            <option value="trial">Sınaq (pulsuz)</option>
            <option value="active">Aktiv</option>
            <option value="frozen">Dondurulub</option>
          </select>
        </label>
        {status === 'trial' && (
          <label className="field">
            Sınağın son tarixi
            <input type="date" lang="az" value={trialUntil} onChange={(e) => setTrialUntil(e.target.value)} disabled={busy} />
          </label>
        )}
        <label className="field">
          Endirim, %
          <input inputMode="decimal" value={discount} onChange={(e) => setDiscount(e.target.value)} disabled={busy} />
        </label>
      </div>
      <fieldset className="fieldset">
        <legend>Əlavə vahidlər (paketin üstünə)</legend>
        <div className="limit-grid">
          {(Object.keys(ADDON_LABEL) as AdminAddonCode[]).map((k) => (
            <label key={k} className="field">
              {ADDON_LABEL[k]}
              <input inputMode="numeric" value={addons[k]} onChange={(e) => setAddons({ ...addons, [k]: e.target.value })} disabled={busy} />
            </label>
          ))}
        </div>
      </fieldset>
      <fieldset className="fieldset">
        <legend>Modullar (paketdən gələnlər işarəli; əlavə və çıxarma yalnız bu obyekt üçündür)</legend>
        {!mods && <p className="muted small">Yüklənir…</p>}
        {mods && (
          <div className="check-grid">
            {mods.map((m) => {
              const on = dirty[m.code] ?? m.open;
              const changed = on !== m.inPlan;
              return (
                <label key={m.code} className="check">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) => setDirty({ ...dirty, [m.code]: e.target.checked })}
                    disabled={busy}
                  />
                  <span>
                    {m.name}
                    {changed && (
                      <span className="muted small">
                        {' '}
                        {on ? `(+ əlavə${m.priceMonth ? `, ${m.priceMonth.replace('.', ',')} ₼/ay` : ''})` : '(paketdən çıxarılıb)'}
                      </span>
                    )}
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </fieldset>
      {status === 'frozen' && <p className="muted small">Dondurmada kassada satış dayanmır — yalnız idarə funksiyaları məhdudlaşır.</p>}
      {error && <p className="bad" role="alert">{error}</p>}
      <div className="form-actions">
        <button type="submit" className="btn-primary btn-small" disabled={busy}>
          {busy ? 'Saxlanılır…' : 'Saxla'}
        </button>
        <button type="button" className="btn-small" onClick={onCancel} disabled={busy}>
          Ləğv et
        </button>
      </div>
    </form>
  );
}
